import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { getNavigationStats } from '../../actionCreators/stats';
import {
  INavigationStats,
  IStripeRequirements,
  ISystem,
} from '../../ts/interface/system.interface';
import { useParams } from 'react-router-dom';
import { changeTab } from '../../utils';
import useStripeAccountInfo from '../../hooks/system/query/useStripeAccountInfo';
import useRates from '../../hooks/rates/query/useRates';
import RatesV2 from '../ratesv2';
import Transactions from '../transactions';
import Coupons from '../coupons/couponIndex';
import Disputes from './disputesTable';
import PendingCharges from './pendingCharges';
import Fees from './fees';
import BillingDash from './dashboard';
import IssuingCardsTable from './issuing/issuingCardIndex';
import { IAdmin } from '../../ts/interface/admins.interface';
import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import usePendingCharges from '../../hooks/rentals/query/usePendingCharges';
import { IDispute } from '../../ts/interface/transaction.interface';
import useDisputes from '../../hooks/transaction/query/useDisputes';
import { billingWarning } from './billingWarning';
interface IBillingProps {
  system: ISystem;
  systemLoaded: boolean;
  navigationStats: INavigationStats;
}

const Billing = ({ system, systemLoaded, navigationStats }: IBillingProps) => {
  let { currentTab = 'overview' } = useParams<{ currentTab: string }>();
  const [currentTabV, setCurrentTabV] = useState(currentTab);
  const handleTabsChange = useCallback(
    (event: React.SyntheticEvent, value: React.SetStateAction<string>) => {
      changeTab(value, currentTab, 'billing');
    },
    [currentTab]
  );
  const { data: newRates } = useRates({
    enabled: systemLoaded,
  }) as { data: unknown[] | undefined };

  useEffect(() => {
    if (currentTab !== currentTabV) {
      setCurrentTabV(currentTab);
    }
  }, [currentTab, currentTabV]);

  const { data: stripeRequirements, isLoading: isStripeAccountInfoLoading } =
    useStripeAccountInfo(null, {
      enabled: systemLoaded,
    }) as { data: IStripeRequirements | undefined; isLoading: boolean };

  const { data: disputes = [] } = useDisputes({
    enabled: systemLoaded,
  });
  const { data: pendingCharges = [] } = usePendingCharges({
    enabled: systemLoaded,
  });

  // Reported up by the Transactions tab (which pages its own data) so the
  // historic-billing warning below can tell whether any transactions exist.
  const [transactionCount, setTransactionCount] = useState<number | undefined>(undefined);

  const currentOpenDisputes = useMemo(() => {
    return disputes.filter(
      (dispute: IDispute) =>
        dispute.system === system.id &&
        (dispute.status === 'Needs Response' ||
          dispute.status === 'Under Review' ||
          dispute.status === 'Warning Under Review' ||
          dispute.status === 'Warning Needs Response')
    );
  }, [disputes, system.id]);

  const warning = () =>
    billingWarning({
      system,
      systemLoaded,
      navigationStats,
      stripeRequirements,
      isStripeAccountInfoLoading,
      transactionCount,
      rateCount: newRates?.length ?? 0,
    });

  return (
    <PageContainer warningMessage={warning()} spaceName={'Billing'}>
      <PageContent
        detailedView={false}
        itemName={'Billing'}
        currentTabV={currentTabV}
        handleTabsChange={handleTabsChange}
        tabsList={[
          { label: 'Overview', value: 'overview' },
          {
            label: 'Pending Charges',
            value: 'pending',
            warnings: pendingCharges?.length,
          },
          { label: 'Rates', value: 'rates' },
          { label: 'Fees', value: 'fees' },
          { label: 'Coupons', value: 'coupons' },
          { label: 'Transactions', value: 'transactions' },
          {
            label: 'Disputes',
            value: 'disputes',
            warnings: currentOpenDisputes.length,
          },
          { label: 'Issuing Cards', value: 'issuing' },
        ]}
        tabsChildren={
          <>
            {currentTab === 'overview' && <BillingDash />}
            {currentTab === 'pending' && <PendingCharges />}
            {currentTab === 'rates' && <RatesV2 />}
            {currentTab === 'fees' && <Fees />}
            {currentTab === 'coupons' && <Coupons />}
            {currentTab === 'transactions' && (
              <Transactions system={system} onTotalCountChange={setTransactionCount} />
            )}
            {currentTab === 'disputes' && <Disputes />}
            {currentTab === 'issuing' && <IssuingCardsTable />}
          </>
        }
      />
    </PageContainer>
  );
};

export default connect(
  (state: {
    admin: {
      admin: IAdmin;
    };
    system: {
      current: ISystem;
      isLoaded: boolean;
    };
    stats: {
      navigation: INavigationStats;
    };
  }) => ({
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
    navigationStats: state.stats.navigation,
  }),
  (dispatch: Function) => ({
    getNavigationStats() {
      dispatch(getNavigationStats());
    },
  })
)(Billing);
